import axios from "axios";
import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";

import {
  API_LEAVE_APPROVE_SQE,
  API_SQE_HR_PENDING_LEAVES,
  GET_LEAVE_REQUESTS_APPROVE_HR,
  LEAVE_APPROVE_MANAGER,
  LEAVE_APPROVE_MANAGER_SQE,
} from "../../config/Endpoints";
import { dispatchEvent } from "../../Hooks/useEvent";
import { Event } from "../../config/events";
import { getUser } from "../../utils/Storage";
import { getApi } from "../../utils/api";
import { getApiErrorMessage } from "../../v2/utils/api-error-message.ts";
import { showError, showSuccess } from "../../v2/utils/toast.ts";
import { useUser } from "../../v2/lib/rbac/useUser.ts";
import { useSqeLabs } from "../../v2/hooks/use-sqe-labs.ts";
import { requestStatusOptions } from "../../constants/requestStatusOptions";
import LeaveApprovalView from "./components/LeaveApprovalView";
import SqeWfhExceededApproval from "./components/SqeWfhExceededApproval";

const statusOptions = [
  ...requestStatusOptions,
  { value: "WITHDRAW", label: "Withdrawn" },
  { value: "MANAGER_PENDING", label: "Pending (Manager)" },
];

const approvalSchema = Yup.object().shape({
  leaveAction: Yup.string().required("Action is required"),
  reason: Yup.string().trim().min(5, "Reason must be at least 5 characters long.").required("Reason is required."),
});

const HrFinalApproveLeave = ({ title, description, viewToggle }) => {
  const [leaveList, setLeaveList] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [leaveAction, setLeaveAction] = useState("");
  const [reason, setReason] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("PENDING");
  const [employeeId, setEmployeeId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [errors, setErrors] = useState({});
  const accessToken = Cookies.get("accessToken");
  const userData = JSON.parse(getUser("userData"));
  const userId = userData?.id;
  const isSqeLabs = useSqeLabs();

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    const statusQuery = selectedStatus === "MANAGER_PENDING"
      ? "&managerStatus=PENDING"
      : selectedStatus ? `&status=${selectedStatus}` : "";
    const endpoint = isSqeLabs
      ? API_SQE_HR_PENDING_LEAVES(userId)
      : `${GET_LEAVE_REQUESTS_APPROVE_HR}/${userId}/pending`;
    try {
      const employeeQuery = employeeId ? `&empId=${encodeURIComponent(employeeId)}` : "";
      const res = await getApi(`${endpoint}?size=${limit}&page=${currentPage - 1}${statusQuery}${employeeQuery}`, accessToken);
      if (res.status === 200) {
        const page = res.data?.data;
        setLeaveList(Array.isArray(page?.content) ? page.content : []);
        setTotalLeaves(page?.totalElements ?? 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, currentPage, employeeId, isSqeLabs, limit, selectedStatus, userId]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  const closeModal = () => {
    setSelectedLeave(null);
    setReason("");
    setLeaveAction("");
    setErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await approvalSchema.validate({ leaveAction, reason }, { abortEarly: false });
      setIsLoading(true);

      const isApprove = leaveAction === "APPROVED";
      const approveUrl = isSqeLabs
        ? `${LEAVE_APPROVE_MANAGER_SQE}/${selectedLeave.requestId}/approve`
        : `${GET_LEAVE_REQUESTS_APPROVE_HR}/${selectedLeave.requestId}/approve`;
      const rejectUrl = isSqeLabs
        ? `${API_LEAVE_APPROVE_SQE}/${selectedLeave.requestId}/reject`
        : `${LEAVE_APPROVE_MANAGER}/${selectedLeave.requestId}/reject`;
      const payload = isApprove
        ? { managerId: userId, comment: reason }
        : { managerId: userId, reason };
      const response = await axios.post(isApprove ? approveUrl : rejectUrl, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      closeModal();
      showSuccess(response?.data?.message || "Leave updated successfully.");
      await fetchList();
      dispatchEvent(Event.SIDEBAR.NOTIFY_EVENT);
    } catch (error) {
      if (error.name === "ValidationError") {
        const fieldErrors = {};
        error.inner.forEach((item) => { fieldErrors[item.path] = item.message; });
        setErrors(fieldErrors);
      } else {
        showError(getApiErrorMessage(error, "Something went wrong. Please try again."));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const restrictReasonKeyPress = (event) => {
    if (event.charCode === 32 && event.target.selectionStart === 0) event.preventDefault();
    if (!/^[a-zA-Z0-9 ]*$/.test(String.fromCharCode(event.charCode))) event.preventDefault();
  };

  return <LeaveApprovalView
    title={title || "Final Approve Leave"} description={description} rows={leaveList} totalItems={totalLeaves}
    loading={isLoading} page={currentPage} pageSize={limit} onPageChange={setCurrentPage}
    onPageSizeChange={(size) => { setLimit(size); setCurrentPage(1); }}
    employeeId={employeeId} onEmployeeChange={(value) => { setEmployeeId(value); setCurrentPage(1); }}
    status={selectedStatus} statusOptions={statusOptions}
    onStatusChange={(value) => { setSelectedStatus(value); setCurrentPage(1); }}
    viewToggle={viewToggle}
    isSqe={isSqeLabs}
    showManager showAction={selectedStatus === "PENDING"} canAct={(request) => request.status === "PENDING"}
    selectedLeave={selectedLeave} onSelectLeave={(request) => { setSelectedLeave(request); setErrors({}); }} onCloseModal={closeModal}
    action={leaveAction} onActionChange={(value) => { setLeaveAction(value); setErrors({}); }}
    reason={reason} onReasonChange={(value) => { setReason(value); setErrors((previous) => ({ ...previous, reason: "" })); }}
    reasonError={errors.reason} onReasonKeyPress={restrictReasonKeyPress} onSubmit={handleSubmit}
  />;
};

const FinalApproveLeave = (props) => {
  const user = useUser();
  const isSqeLabs = useSqeLabs();
  const [view, setView] = useState("HR_REQUESTS");
  const canViewExceededWfh = isSqeLabs && user?.roles?.includes("SUPERADMIN");
  const viewToggle = canViewExceededWfh ? {
    active: view === "EXCEEDED_WFH",
    onCheckedChange: (checked) => setView(checked ? "EXCEEDED_WFH" : "HR_REQUESTS"),
  } : undefined;

  if (canViewExceededWfh && view === "EXCEEDED_WFH") {
    return <SqeWfhExceededApproval {...props} viewToggle={viewToggle} />;
  }

  return <HrFinalApproveLeave {...props} viewToggle={viewToggle} />;
};

export default FinalApproveLeave;
